/**
 * CrearGrupoModal — Modal único para crear un grupo (con espejo automático a Drive).
 *
 * v8.5.14 · Extraído de Tareas/index.jsx para que sea la ÚNICA superficie de
 * creación de grupos del sistema. Antes había 3 implementaciones distintas
 * (esta completa con los 5 tipos, un "+" inline en CrearTareaModal sin tipo
 * — quedaba siempre ad-hoc —, y un selector parcial en PedidosCliente con
 * solo ad-hoc/individual). Ahora los 3 puntos de entrada usan este mismo
 * componente (o su lógica de tipos/derivación vía TIPOS_GRUPO y
 * deriveMesAnio) para que un grupo creado desde cualquier lado tenga
 * exactamente el mismo tipo, validación y espejo a Drive.
 */
import { useState } from "react";
import Glifo from "./Glifo.jsx";
import { X, Loader2, Sparkles } from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { cn } from "../lib/utils.js";
import { Button } from "./ui/button.jsx";
import { Input } from "./ui/input.jsx";

export const TIPOS_GRUPO = [
  { value: "mensual",    label: "Mensual",     emoji: "📅", desc: "Plan de posteos del mes" },
  { value: "campaña",    label: "Campaña",     emoji: "🎯", desc: "Arco narrativo (puede cruzar meses)" },
  { value: "lote-fecha", label: "Lote · Fecha", emoji: "🎉", desc: "Para una fecha clave / efeméride" },
  { value: "individual", label: "Individual",  emoji: "✏️", desc: "Posteo individual fuera de plan" },
  { value: "ad-hoc",     label: "Ad-hoc",      emoji: "📦", desc: "Tareas sueltas sin plan IA" },
];

const MES_ES = { enero:"01",febrero:"02",marzo:"03",abril:"04",mayo:"05",junio:"06",julio:"07",agosto:"08",septiembre:"09",setiembre:"09",octubre:"10",noviembre:"11",diciembre:"12" };

/**
 * deriveMesAnio — misma canonicalización de mes/año sin importar desde dónde
 * se cree el grupo: primero un mes explícito (input type="month"), si no,
 * lo parsea del nombre (mes en español + año de 4 dígitos), si no, lo saca
 * de una fecha de referencia (fecha_inicio de campaña / fecha_evento de lote).
 */
export function deriveMesAnio({ nombre = "", mesInput = "", fechaRef = "" } = {}) {
  let mes = null, anio = null;
  if (mesInput) {
    [anio, mes] = mesInput.split("-");
  } else {
    const n = nombre.toLowerCase();
    for (const [k, v] of Object.entries(MES_ES)) {
      if (n.includes(k)) { mes = v; break; }
    }
    const ym = nombre.match(/\b(20\d{2})\b/);
    if (ym) anio = ym[1];
  }
  if (fechaRef && (!mes || !anio)) {
    anio = anio || fechaRef.slice(0, 4);
    mes  = mes  || fechaRef.slice(5, 7);
  }
  return { mes, anio };
}

export function tipoRequiereFecha(tipo) {
  return tipo === "mensual" || tipo === "campaña" || tipo === "lote-fecha";
}

export function CrearGrupoModal({ clientes, defaultClienteId, onClose, onCreated }) {
  const [form, setForm] = useState({
    cliente_id: defaultClienteId || "",
    nombre: "",
    tipo: "ad-hoc",
    descripcion: "",
    mes: "",            // YYYY-MM (input type="month")
    fecha_inicio: "",
    fecha_fin: "",
    fecha_evento: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Validar requeridos según tipo
  const baseValid = form.cliente_id && form.nombre.trim().length >= 2;
  let tipoValid = true;
  if (form.tipo === "mensual"    && !form.mes)                                   tipoValid = false;
  if (form.tipo === "campaña"    && !form.fecha_inicio)                          tipoValid = false;
  if (form.tipo === "lote-fecha" && !form.fecha_evento && !form.fecha_inicio)    tipoValid = false;
  const canSave = baseValid && tipoValid;

  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      const { mes, anio } = deriveMesAnio({
        nombre: form.nombre,
        mesInput: form.mes,
        fechaRef: form.fecha_inicio || form.fecha_evento,
      });

      const nuevo = await api.createGrupo({
        cliente_id: form.cliente_id,
        nombre: form.nombre.trim(),
        tipo: form.tipo,
        descripcion: form.descripcion || null,
        mes, anio,
        fecha_inicio: form.fecha_inicio || null,
        fecha_fin:    form.fecha_fin    || null,
        fecha_evento: form.fecha_evento || null,
        estado: "activo",
      });
      onCreated(nuevo?.grupo || nuevo);
    } catch (e) {
      setErr(e.message || "Error creando grupo");
    } finally {
      setSaving(false);
    }
  };

  const tipoSel = TIPOS_GRUPO.find(t => t.value === form.tipo);

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-base font-bold">Crear grupo nuevo</h2>
          <Button onClick={onClose} variant="ghost" size="icon" aria-label="Cerrar"><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Cliente *</label>
            <select value={form.cliente_id}
                    onChange={e => setForm(f => ({ ...f, cliente_id: e.target.value }))}
                    className="w-full h-9 px-3 rounded-lg bg-white border border-border text-sm">
              <option value="">Elegí cliente…</option>
              {(clientes || [])
                .filter(c => c.estado === "activo")
                .sort((a, b) => a.nombre.localeCompare(b.nombre))
                .map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Tipo de grupo *</label>
            <div className="grid grid-cols-1 gap-1">
              {TIPOS_GRUPO.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, tipo: t.value }))}
                  className={cn(
                    "text-left px-2.5 py-1.5 rounded-lg border text-xs flex items-center gap-2 transition",
                    form.tipo === t.value ? "border-accent bg-accent/10" : "border-border hover:bg-muted/30"
                  )}
                >
                  <Glifo g={t.emoji} className="text-[20px]" />
                  <span className="font-semibold w-24">{t.label}</span>
                  <span className="text-muted-foreground flex-1">{t.desc}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Nombre del grupo *</label>
            <Input value={form.nombre}
                   onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                   placeholder={form.tipo === "campaña" ? "ej: Campaña Día del Padre" : form.tipo === "lote-fecha" ? "ej: Lote Día del Amigo" : "ej: Plan de Posteos Julio 2026"}
                   autoFocus />
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Descripción (opcional)</label>
            <textarea rows={2} value={form.descripcion}
                      onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                      placeholder="¿De qué se trata este grupo?"
                      className="w-full px-3 py-2 rounded-lg bg-white border border-border text-sm resize-none" />
          </div>

          {/* Campos condicionales por tipo */}
          {form.tipo === "mensual" && (
            <div>
              <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Mes del plan *</label>
              <Input type="month" value={form.mes}
                     onChange={e => setForm(f => ({ ...f, mes: e.target.value }))}
                     className="w-full h-9 px-2 rounded-lg bg-white" />
            </div>
          )}

          {form.tipo === "campaña" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Inicio campaña *</label>
                <Input type="date" value={form.fecha_inicio}
                       onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))}
                       className="w-full h-9 px-2 rounded-lg bg-white" />
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Fin campaña</label>
                <Input type="date" value={form.fecha_fin}
                       onChange={e => setForm(f => ({ ...f, fecha_fin: e.target.value }))}
                       className="w-full h-9 px-2 rounded-lg bg-white" />
              </div>
            </div>
          )}

          {form.tipo === "lote-fecha" && (
            <div>
              <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Fecha del evento *</label>
              <Input type="date" value={form.fecha_evento}
                     onChange={e => setForm(f => ({ ...f, fecha_evento: e.target.value }))}
                     className="w-full h-9 px-2 rounded-lg bg-white" />
            </div>
          )}

          {/* Ad-hoc e Individual: opcionalmente fechas */}
          {(form.tipo === "ad-hoc" || form.tipo === "individual") && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Inicio (opcional)</label>
                <Input type="date" value={form.fecha_inicio}
                       onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))}
                       className="w-full h-9 px-2 rounded-lg bg-white" />
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground/80 mb-1">Fin (opcional)</label>
                <Input type="date" value={form.fecha_fin}
                       onChange={e => setForm(f => ({ ...f, fecha_fin: e.target.value }))}
                       className="w-full h-9 px-2 rounded-lg bg-white" />
              </div>
            </div>
          )}

          <div className="text-[12px] bg-[#E0F7FB] border border-[#A5F3FC] rounded-lg p-2.5 text-[#164E63] flex items-start gap-1.5">
            <Sparkles className="h-3 w-3 mt-0.5" />
            <span>
              Drive: <code className="bg-[#CFFAFE] px-1 rounded">
                {tipoSel?.value === "campaña"    ? `Gestión / {año} / Campañas / ${form.nombre || "{grupo}"}` :
                 tipoSel?.value === "lote-fecha" ? `Gestión / {año} / Lotes / ${form.fecha_evento || "{fecha}"}-${form.nombre || "{grupo}"}` :
                 tipoSel?.value === "mensual"    ? `Gestión / {año} / {mes} / ${form.nombre || "{grupo}"}` :
                 tipoSel?.value === "individual" ? `Gestión / {año} / {mes} / Individuales / ${form.nombre || "{grupo}"}` :
                                                   `Gestión / {año} / Ad-hoc / ${form.nombre || "{grupo}"}`}
              </code>
            </span>
          </div>
          {err && (
            <div className="text-xs bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] rounded p-2">{err}</div>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-muted/20 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button size="sm" onClick={submit} disabled={!canSave || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear grupo"}
          </Button>
        </div>
      </div>
    </div>
  );
}
